import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/features/auth';
import { cn } from '@/lib/utils';
import { type Language } from '@/types';
import {
  type ApprovalJob,
  approveJob,
  loadPendingApprovalJobs,
} from '../api';

interface ApprovalWorkspaceProps {
  language: Language;
}

type DateBucket = 'today' | 'yesterday' | 'thisWeek' | 'older';

const BUCKET_ORDER: DateBucket[] = ['today', 'yesterday', 'thisWeek', 'older'];

// ── Paper command center inks ────────────────────────────────────────────────
const INK_STRONG = '#073331';
const INK_BODY = '#2E4640';
const INK_MUTED = '#607C6E';
const BRONZE = '#8A6A47';
const OK = '#1B7F4D';
const OK_TINT = 'rgba(27,127,77,0.10)';
const WARN = '#B97E1F';
const WARN_TINT = 'rgba(185,126,31,0.12)';
const CRIT = '#C24138';
const CRIT_TINT = 'rgba(194,65,56,0.12)';
const CREAM = '#F0EEE6';

const copy = {
  sv: {
    title: 'Godkänn jobb',
    subtitle: 'Granska och godkänn slutförda städningar.',
    refresh: 'Uppdatera',
    loading: 'Laddar…',
    empty: 'Inga jobb väntar godkännande.',
    today: 'Idag',
    yesterday: 'Igår',
    thisWeek: 'Denna vecka',
    older: 'Äldre',
    pendingTab: 'Väntar',
    approvedTab: 'Godkända',
    searchPlaceholder: 'Sök BRF, städare…',
    cleanerFilter: 'Städare',
    siteFilter: 'BRF',
    allCleaners: 'Alla städare',
    allSites: 'Alla BRF',
    approveSelected: 'Godkänn markerade',
    approving: 'Godkänner…',
    approve: 'Godkänn',
    selectAll: 'Markera alla',
    blocks: 'block',
    points: 'p',
    skipped: 'överhoppade',
    notes: 'noter',
    photos: 'bild',
    stale: 'Äldre än 7 dagar',
    staleDays: (n: number) => `Slutförd för ${n} dagar sedan — väntar fortfarande på godkännande.`,
    monthFilter: 'Månad',
    allMonths: 'Alla månader',
    nothingHere: 'Inget i denna kategori.',
    cleanerJobsCount: (n: number) => `${n} jobb`,
    selected: 'markerade',
    headerLabel: 'GODKÄNN — ARBETSFLÖDE',
  },
  en: {
    title: 'Approve jobs',
    subtitle: 'Review and approve completed cleanings.',
    refresh: 'Refresh',
    loading: 'Loading…',
    empty: 'No jobs pending approval.',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    older: 'Older',
    pendingTab: 'Pending',
    approvedTab: 'Approved',
    searchPlaceholder: 'Search BRF, cleaner…',
    cleanerFilter: 'Cleaner',
    siteFilter: 'BRF',
    allCleaners: 'All cleaners',
    allSites: 'All BRFs',
    approveSelected: 'Approve selected',
    approving: 'Approving…',
    approve: 'Approve',
    selectAll: 'Select all',
    blocks: 'blocks',
    points: 'pts',
    skipped: 'skipped',
    notes: 'notes',
    photos: 'photo',
    stale: 'Older than 7 days',
    staleDays: (n: number) => `Completed ${n} days ago — still awaiting approval.`,
    monthFilter: 'Month',
    allMonths: 'All months',
    nothingHere: 'Nothing in this bucket.',
    cleanerJobsCount: (n: number) => `${n} jobs`,
    selected: 'selected',
    headerLabel: 'APPROVE — WORKFLOW',
  },
} as const;

const ALL = '__all__';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function startOfWeekIso(): string {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

function bucketFor(scheduledFor: string): DateBucket {
  const today = todayIso();
  const yesterday = yesterdayIso();
  const weekStart = startOfWeekIso();
  if (scheduledFor === today) return 'today';
  if (scheduledFor === yesterday) return 'yesterday';
  if (scheduledFor >= weekStart) return 'thisWeek';
  return 'older';
}

function isStale(scheduledFor: string): boolean {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  return new Date(scheduledFor) < cutoff;
}

function daysSince(scheduledFor: string): number {
  const ms = Date.now() - new Date(scheduledFor).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function monthKeyOf(scheduledFor: string): string {
  return scheduledFor.slice(0, 7);
}

function monthLabel(yyyymm: string, language: Language): string {
  const [y, m] = yyyymm.split('-');
  const monthIdx = Number.parseInt(m, 10) - 1;
  const date = new Date(Date.UTC(Number.parseInt(y, 10), monthIdx, 1));
  return date.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// ── Status chip — paper command center inks ─────────────────────────────────
function Chip({
  children,
  ink,
  tint,
}: {
  children: ReactNode;
  ink: string;
  tint: string;
}) {
  return (
    <span
      className="cmd-label inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[8px] leading-none"
      style={{ background: tint, color: ink }}
    >
      {children}
    </span>
  );
}

type JobRowProps = {
  job: ApprovalJob;
  language: Language;
  selected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  isApproving: boolean;
  showCheckbox: boolean;
};

function JobRow({ job, language, selected, onToggleSelect, onApprove, isApproving, showCheckbox }: JobRowProps) {
  const [expanded, setExpanded] = useState(false);
  const text = copy[language];
  const completedBlocks = job.blocks.filter((b) => b.status === 'completed').length;
  const skippedBlocks = job.blocks.filter((b) => b.status === 'skipped').length;
  const blocksWithNotes = job.blocks.filter((b) => b.completion_notes).length;
  const stale = isStale(job.scheduled_for);
  const ageDays = daysSince(job.scheduled_for);
  const ageCrit = ageDays > 21;
  const isApproved = job.status === 'approved';

  return (
    <div
      className={cn(
        'border-b border-[#073331]/10 transition-colors',
        selected && 'bg-[#073331]/[0.04]',
      )}
    >
      <div className="flex items-start gap-2 px-2 py-1.5">
        {showCheckbox && !isApproved && (
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            className="mt-1 border-[#073331]/30 data-[state=checked]:border-[#073331] data-[state=checked]:bg-[#073331]"
            aria-label="Markera"
          />
        )}
        <button
          type="button"
          className="group flex-1 min-w-0 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="text-xs font-mono text-[#2E4640] group-hover:text-[#073331] transition-colors truncate">
              {job.site_name}
            </span>
            <div className="flex items-center gap-1.5 shrink-0">
              {stale && !isApproved && (
                <span
                  className="cmd-label rounded px-1 py-0.5 text-[8px] leading-none cursor-help"
                  style={{
                    background: ageCrit ? CRIT_TINT : WARN_TINT,
                    color: ageCrit ? CRIT : WARN,
                  }}
                  title={text.staleDays(ageDays)}
                  onClick={(event) => event.stopPropagation()}
                >
                  {ageDays}D
                </span>
              )}
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-[#607C6E] group-hover:text-[#073331] transition-colors" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-[#607C6E] group-hover:text-[#073331] transition-colors" />
              )}
            </div>
          </div>
          <div className="cmd-label mt-0.5 truncate normal-case tracking-normal text-[10px]">
            {job.assigned_user_name} · <span className="font-mono tabular-nums">{job.scheduled_for}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1 pt-1">
            <Chip ink={completedBlocks === job.blocks.length ? OK : INK_MUTED} tint={completedBlocks === job.blocks.length ? OK_TINT : 'rgba(96,124,110,0.10)'}>
              <span className="font-mono tabular-nums">{completedBlocks}/{job.blocks.length}</span> {text.blocks}
            </Chip>
            {job.total_points > 0 && (
              <Chip ink={INK_STRONG} tint="rgba(7,51,49,0.07)">
                <span className="font-mono tabular-nums">{job.total_points}</span> {text.points}
              </Chip>
            )}
            {skippedBlocks > 0 && (
              <Chip ink={WARN} tint={WARN_TINT}>
                <AlertCircle className="h-2.5 w-2.5" />
                <span className="font-mono tabular-nums">{skippedBlocks}</span> {text.skipped}
              </Chip>
            )}
            {blocksWithNotes > 0 && (
              <Chip ink={BRONZE} tint="rgba(160,123,82,0.12)">
                <span className="font-mono tabular-nums">{blocksWithNotes}</span> {text.notes}
              </Chip>
            )}
          </div>
        </button>
      </div>

      {expanded && (
        <div className="space-y-2 border-t border-[#073331]/10 bg-[#073331]/[0.025] px-3 pb-3 pt-2">
          <div className="space-y-1">
            {job.blocks.map((block) => (
              <div key={block.id} className="flex items-start gap-2 text-xs">
                {block.status === 'completed' && <Check className="mt-0.5 h-3 w-3 shrink-0" style={{ color: OK }} />}
                {block.status === 'skipped' && <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" style={{ color: WARN }} />}
                {block.status === 'cancelled' && <X className="mt-0.5 h-3 w-3 shrink-0 text-[#607C6E]" />}
                <div className="min-w-0 flex-1">
                  <div className={cn('font-mono', block.status === 'cancelled' ? 'text-[#607C6E] line-through' : 'text-[#2E4640]')}>
                    <span>{block.source_code}</span>
                    <span className="text-[#607C6E]"> · {block.area}</span>
                  </div>
                  {block.completion_notes && (
                    <div className="mt-0.5 text-[10px] italic text-[#607C6E]">"{block.completion_notes}"</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {!isApproved && (
            <div className="pt-1">
              <button
                type="button"
                onClick={onApprove}
                disabled={isApproving}
                className="cmd-label inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[10px] transition-colors hover:bg-[#0B423E] disabled:opacity-60"
                style={{ background: INK_STRONG, color: CREAM }}
              >
                {isApproving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                {text.approve}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type ColumnProps = {
  title: string;
  bucket: DateBucket;
  jobs: ApprovalJob[];
  language: Language;
  selectedIds: Set<string>;
  onToggleJob: (id: string) => void;
  onSelectAllInColumn: () => void;
  onApproveOne: (id: string) => void;
  approvingJobId: string | null;
  emptyText: string;
  cleanerCopy: (n: number) => string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

function Column({ title, jobs, language, selectedIds, onToggleJob, onSelectAllInColumn, onApproveOne, approvingJobId, emptyText, cleanerCopy, collapsed, onToggleCollapsed }: ColumnProps) {
  const text = copy[language];
  const grouped = useMemo(() => {
    const map = new Map<string, ApprovalJob[]>();
    for (const job of jobs) {
      const key = job.assigned_user_name;
      const list = map.get(key) ?? [];
      list.push(job);
      map.set(key, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [jobs]);

  return (
    <div
      className={cn(
        'group/col cmd-panel cmd-panel-inner relative flex min-h-[200px] flex-col overflow-hidden rounded',
        'transition-[max-width,min-width,flex-grow] duration-300 ease-in-out',
        'basis-0 flex-shrink',
        collapsed
          ? 'max-w-[2.25rem] min-w-[2.25rem] flex-grow-0 cursor-pointer bg-[#073331]/[0.025] hover:bg-[#073331]/[0.06]'
          : 'max-w-[100%] min-w-[240px] flex-grow',
      )}
      onClick={collapsed ? onToggleCollapsed : undefined}
      role={collapsed ? 'button' : undefined}
      tabIndex={collapsed ? 0 : undefined}
      onKeyDown={(event) => {
        if (collapsed && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onToggleCollapsed();
        }
      }}
      title={collapsed ? `${title} (${jobs.length})` : undefined}
    >
      {/* Collapsed-rail layer — rotated mono stripe carrying name + count */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 flex flex-col items-center gap-2 py-2',
          'transition-opacity duration-300 ease-in-out',
          collapsed ? 'opacity-100 delay-150' : 'opacity-0 delay-0',
        )}
      >
        <ChevronsRight className="h-3.5 w-3.5 shrink-0 text-[#607C6E] transition-colors group-hover/col:text-[#073331]" />
        <div
          className="flex flex-1 items-center justify-center"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          <span className="cmd-label whitespace-nowrap text-[#073331] group-hover/col:text-[#8A6A47]">
            {title}
            <span className="ml-1.5 font-mono tabular-nums text-[#8A6A47]">{jobs.length}</span>
          </span>
        </div>
      </div>

      {/* Expanded content layer */}
      <div
        className={cn(
          'flex h-full min-w-[260px] flex-col',
          'transition-opacity duration-200 ease-in-out',
          collapsed ? 'pointer-events-none opacity-0 delay-0' : 'opacity-100 delay-150',
        )}
        aria-hidden={collapsed}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#073331]/10 px-2 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              className="shrink-0 text-[#607C6E] transition-colors hover:text-[#073331]"
              onClick={(event) => {
                event.stopPropagation();
                onToggleCollapsed();
              }}
              title={language === 'sv' ? 'Minimera' : 'Collapse'}
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </button>
            <span className="cmd-label truncate">{title}</span>
            <span className="font-mono tabular-nums text-[11px] text-[#073331]">{jobs.length}</span>
          </div>
          {jobs.length > 0 && (
            <button
              type="button"
              className="cmd-label shrink-0 text-[#607C6E] transition-colors hover:text-[#8A6A47]"
              onClick={(event) => {
                event.stopPropagation();
                onSelectAllInColumn();
              }}
            >
              {text.selectAll}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {jobs.length === 0 ? (
            <p className="cmd-label py-6 text-center normal-case tracking-normal text-[10px]">{emptyText}</p>
          ) : (
            grouped.map(([cleanerName, cleanerJobs]) => (
              <div key={cleanerName}>
                <div className="flex items-center justify-between gap-2 border-b border-[#073331]/10 bg-[#073331]/[0.03] px-2 py-1">
                  <span className="cmd-label truncate">{cleanerName}</span>
                  <span className="cmd-label shrink-0 font-mono tabular-nums text-[#8A6A47]">{cleanerCopy(cleanerJobs.length)}</span>
                </div>
                {cleanerJobs.map((job) => (
                  <Fragment key={job.id}>
                  <JobRow
                    job={job}
                    language={language}
                    selected={selectedIds.has(job.id)}
                    onToggleSelect={() => onToggleJob(job.id)}
                    onApprove={() => onApproveOne(job.id)}
                    isApproving={approvingJobId === job.id}
                    showCheckbox
                  />
                  </Fragment>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function ApprovalWorkspace({ language }: ApprovalWorkspaceProps) {
  const { session } = useAuth();
  const text = copy[language];
  const [jobs, setJobs] = useState<ApprovalJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [approvingJobId, setApprovingJobId] = useState<string | null>(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<'pending' | 'approved'>('pending');
  const [search, setSearch] = useState('');
  const [cleanerFilter, setCleanerFilter] = useState<string>(ALL);
  const [siteFilter, setSiteFilter] = useState<string>(ALL);
  const [monthFilter, setMonthFilter] = useState<string>(ALL);
  const [manualCollapsed, setManualCollapsed] = useState<Record<DateBucket, boolean | null>>(() => {
    if (typeof window === 'undefined') return { today: null, yesterday: null, thisWeek: null, older: null };
    try {
      const raw = window.localStorage.getItem('approvals.manualCollapsed.v1');
      if (raw) return JSON.parse(raw) as Record<DateBucket, boolean | null>;
    } catch {
      // ignore
    }
    return { today: null, yesterday: null, thisWeek: null, older: null };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('approvals.manualCollapsed.v1', JSON.stringify(manualCollapsed));
    } catch {
      // ignore
    }
  }, [manualCollapsed]);

  const toggleColumnCollapsed = (bucket: DateBucket, currentCollapsed: boolean) =>
    setManualCollapsed((prev) => ({ ...prev, [bucket]: !currentCollapsed }));

  const sessionToken = session?.access_token ?? null;

  const refresh = async (mode: 'load' | 'refresh' = 'refresh') => {
    if (mode === 'load') setIsLoading(true);
    try {
      const next = await loadPendingApprovalJobs(sessionToken);
      setJobs(next);
    } catch (error) {
      console.error('Failed to load approval jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh('load');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const visible = useMemo(() => {
    return jobs.filter((j) => {
      if (tab === 'pending' && j.status !== 'completed') return false;
      if (tab === 'approved' && j.status !== 'approved') return false;
      if (cleanerFilter !== ALL && j.assigned_user_id !== cleanerFilter) return false;
      if (siteFilter !== ALL && j.site_id !== siteFilter) return false;
      if (monthFilter !== ALL && monthKeyOf(j.scheduled_for) !== monthFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!j.site_name.toLowerCase().includes(q) && !j.assigned_user_name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [jobs, tab, cleanerFilter, siteFilter, monthFilter, search]);

  const byBucket = useMemo(() => {
    const map: Record<DateBucket, ApprovalJob[]> = { today: [], yesterday: [], thisWeek: [], older: [] };
    for (const job of visible) {
      map[bucketFor(job.scheduled_for)].push(job);
    }
    return map;
  }, [visible]);

  const cleanerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const job of jobs) map.set(job.assigned_user_id, job.assigned_user_name);
    return [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [jobs]);

  const siteOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const job of jobs) map.set(job.site_id, job.site_name);
    return [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [jobs]);

  const monthOptions = useMemo(() => {
    const set = new Set<string>();
    for (const job of jobs) set.add(monthKeyOf(job.scheduled_for));
    return [...set].sort().reverse();
  }, [jobs]);

  const pendingCount = jobs.filter((j) => j.status === 'completed').length;
  const approvedCount = jobs.filter((j) => j.status === 'approved').length;

  const toggleJob = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAllInBucket = (bucket: DateBucket) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const job of byBucket[bucket]) {
        if (job.status === 'completed') next.add(job.id);
      }
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const approveOne = async (jobId: string) => {
    setApprovingJobId(jobId);
    try {
      await approveJob(jobId, sessionToken);
      await refresh();
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    } catch (error) {
      console.error('Failed to approve job:', error);
    } finally {
      setApprovingJobId(null);
    }
  };

  const approveSelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkApproving(true);
    try {
      for (const id of selectedIds) {
        try {
          await approveJob(id, sessionToken);
        } catch (error) {
          console.error('Bulk approval failed for', id, error);
        }
      }
      await refresh();
      clearSelection();
    } finally {
      setBulkApproving(false);
    }
  };

  const tabBtn = (active: boolean) =>
    cn(
      'cmd-label pb-1.5 -mb-px transition-colors',
      active ? 'text-[#8A6A47]' : 'text-[#607C6E] hover:text-[#073331]',
    );
  const tabStyle = (active: boolean) => ({
    borderBottom: active ? '1.5px solid #8A6A47' : '1.5px solid transparent',
  });

  return (
    <div className="space-y-3 pb-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-semibold text-[#073331]">{text.title}</h1>
          <span className="cmd-label">
            <span className="font-mono tabular-nums text-[#B97E1F]">{pendingCount}</span> {text.pendingTab.toUpperCase()}
          </span>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={isLoading}
          className="cmd-label inline-flex items-center gap-1.5 rounded border border-[#073331]/12 px-3 py-1.5 text-[#607C6E] transition-colors hover:border-[#073331]/30 hover:text-[#073331] disabled:opacity-60"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
          {text.refresh}
        </button>
      </header>

      {/* Controls panel */}
      <div className="cmd-panel cmd-panel-inner rounded p-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Tabs */}
          <div className="flex items-center gap-4 border-b border-[#073331]/10 pr-2">
            <button type="button" onClick={() => setTab('pending')} className={tabBtn(tab === 'pending')} style={tabStyle(tab === 'pending')}>
              {text.pendingTab} <span className="font-mono tabular-nums">({pendingCount})</span>
            </button>
            <button type="button" onClick={() => setTab('approved')} className={tabBtn(tab === 'approved')} style={tabStyle(tab === 'approved')}>
              {text.approvedTab} <span className="font-mono tabular-nums">({approvedCount})</span>
            </button>
          </div>

          <div className="relative min-w-[200px] max-w-xs flex-1">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#607C6E]" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={text.searchPlaceholder}
              className="h-8 border-[#073331]/12 bg-white pl-7 text-xs text-[#2E4640] focus-visible:ring-[#073331]/20"
            />
          </div>

          <Select value={cleanerFilter} onValueChange={setCleanerFilter}>
            <SelectTrigger className="h-8 w-[180px] border-[#073331]/12 bg-white font-mono text-xs text-[#2E4640]">
              <SelectValue placeholder={text.cleanerFilter} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{text.allCleaners}</SelectItem>
              {cleanerOptions.map(([id, name]) => (
                <SelectItem key={id} value={id}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="h-8 w-[200px] border-[#073331]/12 bg-white font-mono text-xs text-[#2E4640]">
              <SelectValue placeholder={text.siteFilter} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{text.allSites}</SelectItem>
              {siteOptions.map(([id, name]) => (
                <SelectItem key={id} value={id}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="h-8 w-[170px] border-[#073331]/12 bg-white font-mono text-xs text-[#2E4640]">
              <SelectValue placeholder={text.monthFilter} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{text.allMonths}</SelectItem>
              {monthOptions.map((key) => (
                <SelectItem key={key} value={key}>{monthLabel(key, language)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk-bar — slim dark instrument strip */}
      {selectedIds.size > 0 && (
        <div className="cmd-panel cmd-panel-inner cmd-dark flex items-center justify-between gap-3 rounded px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-mono tabular-nums text-[15px] text-[#F0EEE6]">{selectedIds.size}</span>
            <span className="cmd-label">{text.selected}</span>
            <button
              type="button"
              onClick={clearSelection}
              className="ml-1 text-[#8FA89B] transition-colors hover:text-[#F0EEE6]"
              title="X"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => void approveSelected()}
            disabled={bulkApproving}
            className="cmd-label inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[10px] transition-colors hover:bg-[#0B423E] disabled:opacity-60"
            style={{ background: 'rgba(240,238,230,0.12)', color: CREAM }}
          >
            {bulkApproving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            {bulkApproving ? text.approving : `${text.approveSelected} (${selectedIds.size})`}
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-7 w-7 animate-spin text-[#607C6E]" />
        </div>
      ) : visible.length === 0 ? (
        <div className="cmd-panel cmd-panel-inner rounded py-10 text-center">
          <p className="cmd-label normal-case tracking-normal text-[11px]">{text.empty}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
          {BUCKET_ORDER.map((bucket) => {
            const manual = manualCollapsed[bucket];
            const isEmpty = byBucket[bucket].length === 0;
            const collapsed = manual === null ? isEmpty : manual;
            // Empty buckets carry no information — don't render dead rails.
            if (isEmpty) return null;
            return (
              <Fragment key={bucket}>
              <Column
                title={text[bucket]}
                bucket={bucket}
                jobs={byBucket[bucket]}
                language={language}
                selectedIds={selectedIds}
                onToggleJob={toggleJob}
                onSelectAllInColumn={() => selectAllInBucket(bucket)}
                onApproveOne={(id) => void approveOne(id)}
                approvingJobId={approvingJobId}
                emptyText={text.nothingHere}
                cleanerCopy={text.cleanerJobsCount}
                collapsed={collapsed}
                onToggleCollapsed={() => toggleColumnCollapsed(bucket, collapsed)}
              />
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
